import { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'

import { Card, Skeleton } from '@repo/ui'
import { SankeyChart } from 'components/analytics/SankeyChart'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useWebsiteFlows } from 'hooks/api'
import type { DayRange } from 'services'

import { DESTINATION_OPTIONS } from '../../TrackedLinks/destinations'
import { formatCount, formatPct, sourceNotice } from '../websiteAnalyticsModel'
import { flowChartData, publishedTitles, topFlowLabel } from '../websitePagesModel'
import { Notice, SectionNote } from './shared'

/**
 * VRG-690 (Spencer, 2026-10-13) — Site flow: the most common paths through vera.inc, entry
 * page → next → next → where the visit went after that, as a Sankey.
 *
 * Needs each visit's pages IN ORDER, which only PostHog has, so it covers visitors with
 * analytics on only and says so in its heading (the consent caveat is on the tooltip, via
 * the glossary). Each column keeps its six busiest pages and folds the rest into "Other
 * pages"; the API does the bucketing (website-pages-flows.ts). The chart is the shared
 * `components/analytics/SankeyChart` that VRG-691 built for the Sales pipeline (one Sankey for
 * the platform, §36). It puts every end node in the right-hand column, so each exit is labelled
 * with where the visit ended ("Left after 2 pages").
 */

const TITLES = publishedTitles(DESTINATION_OPTIONS)
export const SITE_FLOW_AUDIENCE = 'Visitors with analytics on (sample)'
const CHART_HEIGHT = 380
const TOP_LISTED = 5

export const WebsiteFlowsCard = ({ range }: { range: DayRange }) => {
  const { data, isLoading, error } = useWebsiteFlows(range)
  const chart = useMemo(() => (data ? flowChartData(data, TITLES) : null), [data])
  const problem =
    data && data.posthog.status !== 'ok' && data.posthog.status !== 'fixture'
      ? sourceNotice('PostHog', data.posthog)
      : null

  return (
    <Card title="Site flow" action={<InfoTooltip term="website-site-flow" />}>
      <Stack spacing={3} sx={{ mt: 4 }} data-testid="website-flows-card">
        <SectionNote>
          {SITE_FLOW_AUDIENCE}: the most common paths through vera.inc, three pages deep
          {data && data.sessions > 0 ? `, across ${formatCount(data.sessions)} visits` : ''}. Hover a page or a band for
          its count and share.
        </SectionNote>
        {problem ? <Notice severity={data?.posthog.status === 'error' ? 'error' : 'warning'}>{problem}</Notice> : null}
        {isLoading ? (
          // §19: the chart's own footprint, then the list under it.
          <Box data-testid="website-flows-skeleton">
            <Skeleton height={CHART_HEIGHT} />
            <Stack spacing={2} sx={{ mt: 4 }}>
              <Skeleton height={14} lines={3} />
            </Stack>
          </Box>
        ) : error ? (
          <Notice severity="error">Could not load the site flow. Reload the page, or check the API is running.</Notice>
        ) : !data || !chart || data.sessions === 0 ? (
          problem ? null : <SectionNote>No visits with analytics on in this range.</SectionNote>
        ) : (
          <>
            <Box data-testid="website-site-flow">
              <SankeyChart
                ariaLabel={`How visitors move through vera.inc, ${SITE_FLOW_AUDIENCE.toLowerCase()}`}
                nodes={chart.nodes}
                links={chart.links}
                unit="visits"
                height={CHART_HEIGHT}
              />
            </Box>
            <Stack spacing={1.5} data-testid="website-top-flows">
              <Typography variant="body3" sx={{ fontWeight: 600 }}>
                Most common paths
              </Typography>
              {data.topFlows.slice(0, TOP_LISTED).map((f) => (
                <Stack key={`${f.steps.join('|')}|${f.end}`} direction="row" spacing={3} justifyContent="space-between" sx={{ minWidth: 0 }}>
                  <Typography variant="body3" color="text.secondary" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {topFlowLabel(f, TITLES)}
                  </Typography>
                  <Typography variant="body3" sx={{ whiteSpace: 'nowrap' }}>
                    {formatCount(f.sessions)} · {formatPct(data.sessions ? f.sessions / data.sessions : null)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  )
}
